import { useState, type FormEvent } from "react";

const FORM_ID = "subscribe-contact-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dailteroh653ivfvofug";
const TOPICS = ["往期热料存档", "综艺与影视观察", "创作者与行业盘点", "投稿 / 内容线索"];

interface SubscribeFormProps {
  tone?: "light" | "dark";
}

export default function SubscribeForm({ tone = "light" }: SubscribeFormProps) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const dark = tone === "dark";
  const labelCls = dark ? "text-background-200" : "text-foreground-700";
  const fieldCls = dark
    ? "w-full rounded-md border border-background-50/20 bg-background-50/10 px-3 py-2.5 text-sm text-background-50 outline-none transition-colors placeholder:text-background-300 focus:border-accent-400"
    : "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400";

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formEl = event.currentTarget;
    const data = new FormData(formEl);

    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value);
      if (key === "message") {
        payload.append(key, text.slice(0, 500));
      } else if (text.trim()) {
        payload.append(key, text);
      }
    });

    setStatus("loading");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "提交失败，请稍后重试。";
      const flagged = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !flagged) {
        setStatus("success");
        setFormError("");
        formEl.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg === "form data is spam" ? "提交内容未通过校验，请检查后重试。" : serverMsg);
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  if (status === "success") {
    return (
      <div
        className={`flex items-start gap-3 rounded-lg border p-4 ${
          dark ? "border-background-50/20 bg-background-50/10" : "border-primary-200 bg-primary-50"
        }`}
      >
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
            dark ? "bg-accent-500 text-foreground-950" : "bg-primary-500 text-background-50"
          }`}
        >
          <i className="ri-check-line text-lg" />
        </span>
        <div>
          <p className={`text-sm font-semibold ${dark ? "text-background-50" : "text-foreground-950"}`}>
            订阅成功，感谢关注
          </p>
          <p className={`mt-1 text-xs leading-relaxed ${dark ? "text-background-300" : "text-foreground-600"}`}>
            我们会把新的往期存档与话题盘点发送到你留下的邮箱，也会认真阅读每一条内容线索。
          </p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className={`mt-2 text-xs font-medium underline underline-offset-2 ${
              dark ? "text-accent-300" : "text-primary-600"
            }`}
          >
            再提交一次
          </button>
        </div>
      </div>
    );
  }

  return (
    <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-3" noValidate>
      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${FORM_ID}-email`} className={`text-xs font-medium ${labelCls}`}>
          邮箱地址
        </label>
        <input
          id={`${FORM_ID}-email`}
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          className={fieldCls}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${FORM_ID}-topic`} className={`text-xs font-medium ${labelCls}`}>
          关注方向
        </label>
        <select id={`${FORM_ID}-topic`} name="topic" className={fieldCls} defaultValue={TOPICS[0]}>
          {TOPICS.map((topic) => (
            <option key={topic} value={topic} className="text-foreground-900">
              {topic}
            </option>
          ))}
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${FORM_ID}-message`} className={`text-xs font-medium ${labelCls}`}>
          留言 / 内容线索（选填）
        </label>
        <textarea
          id={`${FORM_ID}-message`}
          name="message"
          rows={3}
          maxLength={500}
          placeholder="可留下你希望我们回顾的话题方向，尽量附上可公开查证的来源。"
          className={`${fieldCls} resize-none`}
        />
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="kl-honeypot-field"
      />

      {status === "error" && (
        <p className="rounded-md border border-primary-300 bg-primary-50 px-3 py-2 text-xs text-primary-700">
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60 ${
          dark
            ? "bg-accent-500 text-foreground-950 hover:bg-accent-400"
            : "bg-primary-500 text-background-50 hover:bg-primary-600"
        }`}
      >
        {status === "loading" ? "提交中…" : "订阅并留下线索"}
      </button>
      <p className={`text-[11px] leading-relaxed ${dark ? "text-background-400" : "text-foreground-400"}`}>
        提交即表示同意我们通过邮件与你联系。我们不会向第三方分享你的邮箱。
      </p>
    </form>
  );
}